import React, { useEffect, useState } from 'react';
import { Flex, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { ROUTE } from '@/config';
import { ArrowLeftIcon } from '@/components/Icons';
import { Heading } from '@/components/Heading';

const { Text } = Typography;

interface ErrorFallbackProps {
  error?: Error | unknown;
  componentStack?: string | null;
  resetError?: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, componentStack, resetError }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [eventId, setEventId] = useState<string | null>(null);

  useEffect(() => {
    if (error) {
      const capturedEventId = Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: componentStack || undefined,
          },
        },
      });

      if (typeof capturedEventId === 'string') {
        setEventId(capturedEventId);
      }
    }
  }, [error, componentStack]);

  const handleGoToOrders = (): void => {
    if (resetError) {
      resetError();
    }
    navigate(ROUTE.ORDERS);
  };

  const handleReload = (): void => {
    window.location.reload();
  };

  return (
    <Flex className='min-h-screen flex justify-center items-center bg-gray-50 px-4'>
      <Flex
        vertical
        className='items-center max-w-md w-full bg-white rounded-lg shadow-lg p-8 border border-gray-200'
      >
        {/* Icon */}
        <div className='mb-6 w-20 h-20 rounded-full bg-red-100 flex items-center justify-center'>
          <svg
            className='w-12 h-12 text-red-600'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            aria-hidden='true'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
            />
          </svg>
        </div>

        {/* Title */}
        <Text className='font-bold text-2xl leading-tight text-customTunaPrimary text-center mb-4'>
          {String(t('error.unexpected_title'))}
        </Text>

        {/* Description */}
        <Text className='font-normal text-sm leading-relaxed text-customGrayShade5 text-center mb-6'>
          {String(t('error.unexpected_desc'))}
        </Text>

        {/* Primary Action */}
        <button
          type='button'
          onClick={handleGoToOrders}
          className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors w-full mb-3 justify-center'
          aria-label='Go to Orders page'
        >
          <ArrowLeftIcon />
          <span className='leading-5 uppercase font-bold text-sm'>{String(t('error.go_to_orders'))}</span>
        </button>

        {/* Reload */}
        <button
          type='button'
          onClick={handleReload}
          className='cursor-pointer text-gray-600 hover:text-gray-800 bg-transparent border-none'
          aria-label='Reload page'
        >
          <Text className='text-xs underline'>{String(t('error.reload_page'))}</Text>
        </button>
      </Flex>
    </Flex>
  );
};

export default ErrorFallback;
